import {
	Organization,
	Brand,
	ImageObject,
	PostalAddress,
	ContactPoint,
	WebSite,
	WPHeader,
	WPFooter,
	ItemList,
	SearchAction,
	WebPageElement,
	WebPage,
	CreativeWork,
	BreadcrumbList,
	CollectionPage,
	VideoObject,
} from "schema-dts";

/* =====================
	 INDEX
=======================
0. WithContext Utility Type
1. Basic Info & URLs
2. Social Links
3. Global Schemas
4. Organization Schema
5. Header Schemas (Address, Contact, Website)
6. Navigation Schemas (Nav, WPHeader)
7. Footer Schema (WPFooter)
8. Page Schema (WebPage, Breadcrumb)
9. Single Item Schema (Project, Journal Entry, CreativeWork)
10. CollectionPage Schema (Projects, Journal)
11. Related Content Schema
12 Image & Video Schema
======================= */

/* =====================
  0. WithContext Utility Type
======================= */
type AllowedEntryTypesForCollection = "CreativeWork" | "BlogPosting" | "Project";
export type WithContext<T> = T & { "@context": string };
export type WithType<T> = T & { "@type": string };

/* =====================
  1. Basic Info & URLs
======================= */
export const baseUrl = "https://www.bofill.com/";
export const siteTitle = "BOFILL TALLER DE ARQUITECTURA";
export const siteDescription =
	"An international and interdisciplinary workshop for architecture established in 1963 located in Barcelona.";

/* =====================
  2. Social Links
======================= */
export const linkInstagram = "https://www.instagram.com/bofillarquitectura";
export const linkLinkedIn = "https://www.linkedin.com/company/bofillarquitectura";

export const sameAsSchema: string[] = [
	linkLinkedIn,
	linkInstagram,
];

/* =====================
  3. Global Schemas
======================= */
export const logoSchema: ImageObject = {
	"@type": "ImageObject",
	"@id": `${baseUrl}#logo`,
	url: baseUrl + "favicon.png",
};

export const brandSchema: Brand = {
	"@type": "Brand",
	name: siteTitle,
	logo: { "@id": `${baseUrl}#logo` },
	url: baseUrl,
	sameAs: sameAsSchema,
};

/* =====================
  4. Organization Schema
======================= */
export const organizationSchema: Organization = {
	"@type": "Organization",
	"@id": `${baseUrl}#organization`,
	name: siteTitle,
	legalName: siteTitle,
	url: baseUrl,
	sameAs: sameAsSchema,
	logo: { "@id": `${baseUrl}#logo` },
};

/* =====================
  5. Header Schemas (Address, Contact, Website)
======================= */
export const postalAddressSchema: PostalAddress = {
	"@type": "PostalAddress",
	streetAddress: "Avenida de la Industria 14",
	addressLocality: "Sant Just Desvern",
	addressRegion: "Barcelona",
	postalCode: "08960",
	addressCountry: "ES",
};

export const contactPointSchema: ContactPoint = {
	"@type": "ContactPoint",
	telephone: "+34934999900",
	contactType: "Customer Service",
	availableLanguage: ["Spanish", "English"],
};

export const websiteSchema: WithContext<WebSite> = {
	"@context": "https://schema.org",
	"@type": "WebSite",
	"@id": `${baseUrl}#website`,
	name: siteTitle,
	url: baseUrl,
	description: siteDescription,
	sameAs: sameAsSchema,
	publisher: {
		"@id": `${baseUrl}#organization`,
	},
	potentialAction: {
		"@type": "SearchAction",
		target: `${baseUrl}search?query={search_term_string}`,
		"query-input": "required name=search_term_string",
	} as SearchAction,
};

/* =====================
  6. Navigation Schemas (Nav, WPHeader)
======================= */
export function createNavSchema(menuLinks: Array<{ title: string; url: string }>): ItemList & { "@context": string } {
	return {
		"@context": "https://schema.org",
		"@type": "ItemList",
		"name": "Bofill Main Navigation",
		"@id": `${baseUrl}#navigation`,
		itemListElement: menuLinks.map((link, index) => ({
			"@type": "SiteNavigationElement",
			position: index + 1,
			name: link.title,
			url: `${baseUrl.replace(/\/$/, "")}${link.url}`,
		})),
	};
}

export function createWPHeaderSchema(menuLinks: Array<{ title: string; url: string }>): WPHeader & { "@context": string } {
	return {
		"@context": "https://schema.org",
		"@type": "WPHeader",
		"@id": `${baseUrl}#header`,
		name: "Site Header",
		description: "Main header of the website with logo and navigation",
		url: baseUrl,
		mainEntity: {
			"@id": `${baseUrl}#organization`,
		},
		hasPart: menuLinks.map((link, index) => ({
			"@type": "SiteNavigationElement",
			position: index + 1,
			name: link.title,
			url: `${baseUrl.replace(/\/$/, "")}${link.url}`,
		})),
	};
}

/* =====================
  7. Footer Schema (WPFooter)
======================= */
export function createWPFooterSchema({
	url,
	copyright,
	linksSocial = [],
	subscribeUrl,
	links = [],
}: {
	url: string;
	copyright?: string | null;
	linksSocial?: { title: string; url: string }[];
	subscribeUrl?: string;
	links?: { title: string; url: string }[];
}): WPFooter & { "@context": string } {
	return {
		"@context": "https://schema.org",
		"@type": "WPFooter",
		name: "Site Footer",
		"@id": `${baseUrl}#footer`,
		url: baseUrl,
		copyrightHolder: copyright ?? undefined,
		mainEntity: {
			"@type": "Organization",
			"@id": `${baseUrl}#organization`,
			name: siteTitle,
			url: baseUrl,
		},
		sameAs: linksSocial.map(link => link.url) ?? sameAsSchema,
		...(subscribeUrl && {
			potentialAction: {
				"@type": "SubscribeAction",
				name: "Join the Newsletter",
				target: subscribeUrl,
			},
		}),
		hasPart: links.map((link, index) => ({
			"@type": "SiteNavigationElement",
			position: index + 1,
			name: link.title,
			url: link.url,
		})),
	};
}

/* =====================
  8. Page Schema (WebPage, Breadcrumb)
======================= */
export function createWebPageSchema({
	url,
	name,
	description,
	datePublished,
	dateModified,
	mainEntity,
	hasPart = [],
	publisher = organizationSchema,
	sameAs = sameAsSchema,
	inLanguage = "en"
}: {
	url: string;
	name: string;
	description: string;
	datePublished?: string;
	dateModified?: string;
	mainEntity?: WithType<CreativeWork>;
	hasPart?: WebPageElement[];
	publisher?: typeof organizationSchema;
	sameAs?: string[];
	inLanguage?: string;
}): WebPage & { "@context": string } {
	const pageId = url + '#webpage';
	return {
		"@context": "https://schema.org",
		"@type": "WebPage",
		"@id": pageId,
		url,
		name,
		description,
		datePublished,
		dateModified,
		mainEntity,
		hasPart,
		publisher,
		isPartOf: {
			"@type": "WebSite",
			name: siteTitle,
			url: baseUrl,
			publisher,
		},
		sameAs,
		inLanguage,
	};
}

export function createBreadcrumbList(items: Array<{ name: string; url: string }>, url?: string): WithContext<BreadcrumbList> {
	const breadcrumbId = url + '#breadcrumb';

	return {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		"name": "Bofill Breadcrumbs",
		"@id": breadcrumbId,
		itemListElement: items.map((item, index) => ({
			"@type": "ListItem",
			position: index + 1,
			name: item.name,
			item: item.url,
		})),
	};
}

/* =====================
  9. Single Item Schema (Project, Journal Entry, CreativeWork)
======================= */
export function createCreativeWorkSchema({
	url,
	name,
	description,
	image,
	datePublished,
	inLanguage = "en",
}: {
	url: string;
	name: string;
	description: string;
	image?: string;
	datePublished?: string;
	inLanguage?: string;
}): WithContext<CreativeWork> {
	return {
		"@context": "https://schema.org",
		"@type": "CreativeWork",
		url,
		name,
		description,
		image,
		datePublished,
		author: organizationSchema,
		inLanguage,
	};
}

export function createProjectSchema({
	url,
	name,
	description,
	image,
	datePublished,
	dateModified,
	year,
	type = "CreativeWork", // default fallback
}: {
	url: string;
	name: string;
	description?: string;
	image?: string;
	dateModified?: string;
	datePublished?: string;
	year?: string | number;
	type?: "CreativeWork" | "Article" | "BlogPosting";
}): WithContext<CreativeWork> {
	return {
		"@context": "https://schema.org",
		"@type": type,
		name,
		url,
		...(description && { description }),
		...(image && { image }),
		...(datePublished && { datePublished }),
		...(dateModified && { dateModified }),
		...(datePublished && { dateCreated: datePublished }),
		author: organizationSchema,
	};
}

/* =====================
  10. CollectionPage Schema (Projects, Journal)
======================= */
export function createCollectionPageSchema({
	name,
	description,
	url,
	entries = [],
	typeOfEntries = "CreativeWork" as AllowedEntryTypesForCollection,
	inLanguage = "en",
}: {
	name: string;
	description: string;
	url: string;
	entries?: Array<{ title: string; url: string; image?: string }>;
	typeOfEntries?: AllowedEntryTypesForCollection;
	inLanguage?: string;
}): WithContext<CollectionPage> {
	const pageId = url + '#collection';
	return {
		"@context": "https://schema.org",
		"@type": "CollectionPage",
		"@id": pageId,
		name,
		description,
		url,
		isPartOf: {
			"@type": "WebSite",
			name: siteTitle,
			url: baseUrl,
		},
		inLanguage,
		hasPart: entries.map(entry => ({
			"@type": typeOfEntries,
			name: entry.title,
			url: entry.url,
			...(entry.image && {
				image: { "@type": "ImageObject", url: entry.image }
			}),
		})) as unknown as CreativeWork[],
	};
}


/* =====================
  11. Related Content Schema
======================= */

/**
 * Creates an array of CreativeWork schemas representing related content items,
 * which can be added as 'hasPart' or 'mentions' of a main entity schema (project or journal).
 *
 * @param relatedItems Array of objects containing minimal data to describe related items.
 * @param typeOfEntries The type of CreativeWork, default "CreativeWork" but can be "BlogPosting" or "Project"
 * @returns Array of CreativeWork schemas
 */
export function createRelatedContentSchemas(
	relatedItems: Array<{ title: string; slug: string; featuredImage: { image: { imageUrl: string } } | null }> = [],
	basePath: string,
	type: AllowedEntryTypesForCollection = "CreativeWork"
): CreativeWork[] {
	return relatedItems.map(item => ({
		"@context": "https://schema.org",
		"@type": type,
		name: item.title,
		url: `${baseUrl}${basePath}/${item.slug}`,
		...(item.featuredImage?.image?.imageUrl && {
			image: {
				"@type": "ImageObject",
				url: item.featuredImage.image.imageUrl,
			},
		}),
		author: organizationSchema,
	})) as unknown as CreativeWork[];
}

/* =====================
  12 Image & Video Schema
======================= */

/* =====================
  12.1 Image Schema
======================= */

/**
 * Creates a structured ImageObject schema for SEO and linked data purposes.
 *
 * @param src - The URL of the image
 * @param alt - Alt text or description for the image
 * @param caption - Optional caption text
 * @param filename - Optional original filename (used if alt/caption is missing)
 * @returns ImageObject schema
 */
export function createImageSchema({
	src,
	alt,
	caption,
	filename,
}: {
	src: string;
	alt?: string;
	caption?: string;
	filename?: string;
}): WithContext<ImageObject> {
	return {
		"@context": "https://schema.org",
		"@type": "ImageObject",
		url: src,
		contentUrl: src,
		description: alt || filename,
		...(caption && { caption }),
		creator: { "@id": `${baseUrl}#organization` },
	};
}


/* =====================
  12.2 Image Schema
======================= */

export function createVideoSchema({ src, alt, thumbnailUrl }: {
	src: string; alt?: string; thumbnailUrl?: string;
}): WithContext<VideoObject> {
	return {
		"@context": "https://schema.org",
		"@type": "VideoObject",
		name: alt || "Bofill Taller de Arquitectura | Video",
		description: alt || "",
		thumbnailUrl: thumbnailUrl || "",
		contentUrl: src,
		embedUrl: src,
		publisher: { "@id": `${baseUrl}#organization` },
	};
}